import { useCallback, useEffect, useState, type Dispatch } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import { Check, CreditCard, Lock, Shield } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useProtectedAction, useVaultFeatureLocked } from "@/lib/hooks/use-protected-action";
import { showToast } from "@/lib/ui/toast";
import { isVaultLockedError, reportVaultLocked } from "@/lib/vault-unlock";

import type { CheckoutAction, CheckoutState, SavedCard } from "./checkout-types";

// Source: legacy src/components/billing/checkout/checkout-step-card.tsx (1:1
// port). Differences vs legacy, per SPA conventions: next-intl → Lingui
// macros (the legacy en.json values are the msgids), the raw fetch() of
// GET /api/account/billing/cards → apiFetch + react-query (single attempt,
// errors swallowed like legacy's `.catch(() => {})`), motion.* → m.*, and
// useProtectedAction comes from "@/lib/hooks/use-protected-action" (same
// contract as the legacy "@/hooks/use-protected-action").
//
// VAULT PIN (2026-10): GET /api/account/billing/cards answers 403 vault_locked
// while a PIN guarding the WALLET is not unlocked. The saved cards are then
// not asked for at all: the "Saved cards" option shows locked, and choosing it
// asks for the PIN (wallet or billing, whichever the account protects) and
// only then loads them. A refusal anyway (this tab's unlock was stale) drops
// the tab's hint and shows the option locked again — never an error.

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function brandIcon(brand: string) {
  const b = brand.toLowerCase();
  if (b === "visa") return "Visa";
  if (b === "mastercard") return "Mastercard";
  if (b === "amex" || b === "american_express") return "Amex";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

// Mirrors GET /api/account/billing/cards (apps/api/src/routes/account-billing-cards.ts).
interface SavedCardsResponse {
  cards?: SavedCard[];
  defaultPaymentMethodId?: string | null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutStepCardProps {
  state: CheckoutState;
  dispatch: Dispatch<CheckoutAction>;
  onContinue: () => void;
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutStepCard({ state, dispatch, onContinue, onBack }: CheckoutStepCardProps) {
  const { t } = useLingui();
  // Paying with a saved card is billing; listing them is the wallet.
  const { triggerProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);
  // null while the vault status loads.
  const walletLocked = useVaultFeatureLocked("wallet");

  // --- Saved cards loading ---
  // Captured once at mount (legacy computed it before the []-deps effect).
  const [alreadyHasCards] = useState(() => state.savedCards.length > 0);
  const [showSavedCards, setShowSavedCards] = useState(!!state.selectedCardId);
  const [attempted, setAttempted] = useState(false);

  const cardsQuery = useQuery({
    queryKey: ["api", "/api/account/billing/cards"],
    queryFn: () => apiFetch.get<SavedCardsResponse>("/api/account/billing/cards"),
    enabled: !alreadyHasCards && walletLocked === false,
    retry: false,
  });

  useEffect(() => {
    if (!cardsQuery.data) return;
    const cards: SavedCard[] = cardsQuery.data.cards || [];
    dispatch({ type: "SET_SAVED_CARDS", cards });
  }, [cardsQuery.data, dispatch]);

  // The API holds no wallet unlock for this browser although the tab thought
  // so: drop the hint (walletLocked turns true) — the server is the truth.
  const cardsRefused = isVaultLockedError(cardsQuery.error);
  useEffect(() => {
    if (cardsRefused) reportVaultLocked();
  }, [cardsRefused, cardsQuery.errorUpdatedAt]);

  const refetchCards = cardsQuery.refetch;
  const cardsLocked =
    !alreadyHasCards && !cardsQuery.data && (walletLocked !== false || cardsRefused);
  const cardsLoading = !alreadyHasCards && (walletLocked === null || cardsQuery.isLoading);

  const openSavedCards = useCallback(
    (cards: SavedCard[]) => {
      setShowSavedCards(true);
      // Auto-select first card if none selected
      if (!state.selectedCardId && cards.length > 0) {
        const defaultCard = cards.find((c) => c.isDefault) || cards[0];
        dispatch({ type: "SET_SELECTED_CARD", cardId: defaultCard.id });
      }
    },
    [state.selectedCardId, dispatch],
  );

  const handleShowSavedCards = useCallback(() => {
    triggerProtected(() => {
      if (state.savedCards.length > 0) {
        openSavedCards(state.savedCards);
        return;
      }
      // Locked until now: load them under the fresh unlock, then open. A
      // refusal again (handled above) or a failure leaves the option as it is.
      void refetchCards().then(({ data }) => {
        if (!data) return;
        const cards = data.cards || [];
        if (cards.length === 0) {
          showToast(t`You have no saved cards.`, "info");
          return;
        }
        openSavedCards(cards);
      });
    });
  }, [triggerProtected, state.savedCards, openSavedCards, refetchCards, t]);

  // Validation: must have a selection (new card = null is valid, but if showSavedCards without a card selected = invalid)
  const isValid = state.selectedCardId !== null || !showSavedCards;

  const handleContinue = useCallback(() => {
    if (!isValid) {
      setAttempted(true);
      return;
    }
    onContinue();
  }, [isValid, onContinue]);

  return (
    <m.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      <div className="border-b px-5 py-3">
        <h3 className="text-foreground text-sm font-semibold">
          <Trans>Payment method</Trans>
        </h3>
      </div>

      <div className="space-y-4 p-5">
        {cardsLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-muted h-16 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {/* New card — default option */}
            <m.button
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                dispatch({ type: "SET_SELECTED_CARD", cardId: null });
                setShowSavedCards(false);
                setAttempted(false);
              }}
              className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                state.selectedCardId === null && !showSavedCards
                  ? "border-primary/30 bg-primary/5 ring-primary/20 ring-2"
                  : "border-border hover:border-primary/20"
              }`}
            >
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  state.selectedCardId === null && !showSavedCards
                    ? "border-primary bg-primary"
                    : "border-muted-foreground/30"
                }`}
              >
                {state.selectedCardId === null && !showSavedCards && (
                  <div className="h-2 w-2 rounded-full bg-white" />
                )}
              </div>
              <CreditCard className="text-muted-foreground h-5 w-5 shrink-0" />
              <div className="flex-1">
                <div className="text-foreground text-sm font-medium">
                  <Trans>New card</Trans>
                </div>
                <div className="text-muted-foreground text-xs">
                  <Trans>Pay with a new card via Stripe</Trans>
                </div>
              </div>
            </m.button>

            {/* Saved cards option — shown locked while the wallet PIN hides them */}
            {(state.savedCards.length > 0 || cardsLocked) && (
              <m.button
                whileTap={{ scale: 0.99 }}
                onClick={handleShowSavedCards}
                className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                  showSavedCards
                    ? "border-primary/30 bg-primary/5 ring-primary/20 ring-2"
                    : "border-border hover:border-primary/20"
                }`}
              >
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                    showSavedCards ? "border-primary bg-primary" : "border-muted-foreground/30"
                  }`}
                >
                  {showSavedCards && <div className="h-2 w-2 rounded-full bg-white" />}
                </div>
                <div className="flex-1">
                  <div className="text-foreground text-sm font-medium">
                    <Trans>Saved cards</Trans>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {state.savedCards.length > 0 ? (
                      <>
                        {state.savedCards.length}{" "}
                        {state.savedCards.length === 1 ? t`card` : t`cards`}
                      </>
                    ) : (
                      <Trans>Unlock with your PIN to use a saved card</Trans>
                    )}
                  </div>
                </div>
                {!showSavedCards && <Lock className="text-muted-foreground h-4 w-4" />}
              </m.button>
            )}

            {/* Expanded saved cards list */}
            <AnimatePresence>
              {showSavedCards && (
                <m.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2 overflow-hidden pl-6"
                >
                  {state.savedCards.map((card) => (
                    <m.button
                      key={card.id}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => {
                        dispatch({ type: "SET_SELECTED_CARD", cardId: card.id });
                        setAttempted(false);
                      }}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-all ${
                        state.selectedCardId === card.id
                          ? "border-primary/30 bg-primary/5 ring-primary/20 ring-1"
                          : attempted && !state.selectedCardId
                            ? "border-red-300 dark:border-red-800"
                            : "border-border hover:border-primary/20"
                      }`}
                    >
                      <div
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          state.selectedCardId === card.id
                            ? "border-primary bg-primary"
                            : "border-muted-foreground/30"
                        }`}
                      >
                        {state.selectedCardId === card.id && (
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-foreground text-sm font-medium">
                          <CreditCard className="mr-1.5 inline h-4 w-4 align-text-bottom" />
                          {brandIcon(card.brand)} •••• {card.last4}
                        </div>
                        {card.expMonth && card.expYear && (
                          <div className="text-muted-foreground text-xs">
                            exp {String(card.expMonth).padStart(2, "0")}/
                            {String(card.expYear).slice(-2)}
                          </div>
                        )}
                      </div>
                      {card.isDefault && (
                        <Badge variant="secondary" className="text-[0.625rem]">
                          Default
                        </Badge>
                      )}
                    </m.button>
                  ))}

                  {/* Error: no card selected */}
                  {attempted && !state.selectedCardId && (
                    <m.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs text-red-600 dark:text-red-400"
                    >
                      <Trans>Please select a card to continue.</Trans>
                    </m.p>
                  )}
                </m.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Recurring authorization */}
        <div className="border-border flex items-start gap-3 rounded-xl border p-4">
          <div className="border-primary bg-primary mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2">
            <Check className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-muted-foreground text-sm">
            <Trans>I authorize recurring payments for this subscription</Trans>
          </span>
        </div>

        {/* Stripe disclaimer */}
        <div className="text-muted-foreground flex items-start gap-2 text-[0.625rem] leading-relaxed">
          <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <Trans>
              Payments are securely processed by Stripe, not by PontiMed servers. You can stop
              recurring payments at any time by cancelling your subscription.
            </Trans>
          </span>
        </div>
      </div>

      {/* Footer navigation — desktop only */}
      <div className="hidden gap-3 px-5 pt-4 pb-5 md:flex">
        {onBack && (
          <Button variant="outline" size="lg" onClick={onBack} className="shrink-0">
            <Trans>Back</Trans>
          </Button>
        )}
        <Button
          className="flex-1"
          size="lg"
          onClick={handleContinue}
          disabled={cardsLoading || state.loading}
        >
          <Trans>Continue</Trans>
        </Button>
      </div>

      {/* PIN Gate Modal */}
      {PinGateElement}
    </m.div>
  );
}
